import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Chip,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Grid,
  Paper,
  CircularProgress,
  Divider,
  Autocomplete
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  DIAS_SEMANA,
  MESES,
  toIsoDate,
  parseIso,
  getMonthGridCells,
  getWeekDays,
  getFetchRange,
  navigateAnchor,
  enumerateDates
} from '../../utils/calendarioDateUtils';

const TIPO_LABEL = {
  manual: 'Manual',
  vencimento_contrato: 'Renovação',
  alerta_90: '90 dias',
  alerta_60: '60 dias',
  alerta_30: '30 dias'
};

const TIPO_COR = {
  manual: '#1976d2',
  vencimento_contrato: '#c62828',
  alerta_90: '#e65100',
  alerta_60: '#f57c00',
  alerta_30: '#fbc02d'
};

const FORM_VAZIO = {
  titulo: '',
  descricao: '',
  dataEvento: '',
  dataFim: '',
  numeroContrato: '',
  notificarContratos: true,
  notificarFiscal: true,
  emailsExtras: [],
  usuarioIds: [],
  prestadorIds: []
};

function eventosPorData(eventos) {
  const map = {};
  for (const ev of eventos) {
    const fim = ev.dataFim || ev.dataEvento;
    const dias = enumerateDates(ev.dataEvento, fim);
    for (const k of dias) {
      if (!map[k]) map[k] = [];
      if (!map[k].some((x) => x.id === ev.id)) map[k].push(ev);
    }
  }
  return map;
}

function destinatariosFromEvento(ev) {
  const usuarioIds = [];
  const prestadorIds = [];
  const emailsExtras = [];
  (ev?.destinatarios || []).forEach((d) => {
    if (d.tipo === 'usuario' && d.usuario_id) usuarioIds.push(Number(d.usuario_id));
    if (d.tipo === 'prestador' && d.prestador_id) prestadorIds.push(Number(d.prestador_id));
    if (d.tipo === 'email' && d.email) emailsExtras.push(d.email);
  });
  return { usuarioIds, prestadorIds, emailsExtras };
}

function buildDestinatarios(form) {
  const dest = [];
  (form.usuarioIds || []).forEach((id) => dest.push({ tipo: 'usuario', usuario_id: Number(id) }));
  (form.prestadorIds || []).forEach((id) => dest.push({ tipo: 'prestador', prestador_id: Number(id) }));
  (form.emailsExtras || []).forEach((email) => {
    const e = String(email || '').trim();
    if (e) dest.push({ tipo: 'email', email: e });
  });
  return dest;
}

function EventoChip({ ev, onClick }) {
  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        onClick(ev);
      }}
      sx={{
        fontSize: 10,
        lineHeight: 1.2,
        px: 0.5,
        py: 0.25,
        mb: 0.25,
        borderRadius: 0.5,
        bgcolor: TIPO_COR[ev.tipo] || '#757575',
        color: '#fff',
        cursor: 'pointer',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        '&:hover': { opacity: 0.9 }
      }}
      title={ev.titulo}
    >
      {ev.titulo}
    </Box>
  );
}

export default function CalendarioDialog({
  open,
  onClose,
  fetchEventos,
  fetchUsuarios,
  fetchPrestadores,
  criarEvento,
  atualizarEvento,
  excluirEvento,
  modoPrestador = false,
  contratos = []
}) {
  const hoje = new Date();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const [view, setView] = useState('month');
  const [anchor, setAnchor] = useState(hoje);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [prestadores, setPrestadores] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [painel, setPainel] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [saving, setSaving] = useState(false);

  const range = useMemo(() => getFetchRange(view, anchor), [view, anchor]);
  const mapa = useMemo(() => eventosPorData(eventos), [eventos]);
  const hojeIso = toIsoDate(hoje);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        dataInicio: range.dataInicio,
        dataFim: range.dataFim
      };
      if (range.mes) params.mes = range.mes;
      if (range.ano) params.ano = range.ano;
      const data = await fetchEventos(params);
      setEventos(Array.isArray(data) ? data : []);
    } catch (e) {
      setEventos([]);
      setError(e.message || 'Erro ao carregar eventos do calendário.');
    } finally {
      setLoading(false);
    }
  }, [fetchEventos, range]);

  useEffect(() => {
    if (open) carregar();
  }, [open, carregar]);

  useEffect(() => {
    if (!open || modoPrestador || !fetchUsuarios) return;
    if (!painel || (painel.mode !== 'create' && painel.mode !== 'edit')) return;

    setLoadingCatalog(true);
    setCatalogError('');
    Promise.all([fetchUsuarios(), fetchPrestadores?.() ?? Promise.resolve([])])
      .then(([u, p]) => {
        setUsuarios(Array.isArray(u) ? u : []);
        setPrestadores(Array.isArray(p) ? p : []);
      })
      .catch((e) => {
        setUsuarios([]);
        setPrestadores([]);
        setCatalogError(e.message || 'Erro ao carregar usuários/prestadores.');
      })
      .finally(() => setLoadingCatalog(false));
  }, [open, painel, modoPrestador, fetchUsuarios, fetchPrestadores]);

  const abrirDia = (iso) => {
    setPainel({ mode: 'day', date: iso });
    setError('');
  };

  const abrirCriar = (iso) => {
    setPainel({ mode: 'create', date: iso });
    setForm({ ...FORM_VAZIO, dataEvento: iso, dataFim: iso });
    setError('');
  };

  const abrirEditar = (ev) => {
    if (ev.tipo !== 'manual') {
      setPainel({ mode: 'view', event: ev });
      setError('');
      return;
    }
    const dest = destinatariosFromEvento(ev);
    setPainel({ mode: 'edit', event: ev });
    setForm({
      ...FORM_VAZIO,
      titulo: ev.titulo,
      descricao: ev.descricao || '',
      dataEvento: String(ev.dataEvento).slice(0, 10),
      dataFim: String(ev.dataFim || ev.dataEvento).slice(0, 10),
      numeroContrato: ev.numeroContrato || '',
      ...dest
    });
    setError('');
  };

  const salvar = async () => {
    setError('');
    if (!form.titulo || !form.dataEvento) {
      setError('Título e data inicial são obrigatórios.');
      return;
    }
    if (form.dataFim && form.dataFim < form.dataEvento) {
      setError('Data final não pode ser anterior à data inicial.');
      return;
    }
    setSaving(true);
    try {
      const base = {
        titulo: form.titulo,
        descricao: form.descricao,
        dataEvento: form.dataEvento,
        dataFim: form.dataFim || form.dataEvento,
        numeroContrato: form.numeroContrato || null
      };

      const payload = modoPrestador
        ? {
            ...base,
            notificarContratos: form.notificarContratos,
            notificarFiscal: form.notificarFiscal,
            emailsExtras: form.emailsExtras?.length ? form.emailsExtras : form.emailExtra ? [form.emailExtra] : []
          }
        : {
            ...base,
            destinatarios: buildDestinatarios(form)
          };

      if (painel?.mode === 'edit' && painel.event?.id && atualizarEvento) {
        await atualizarEvento(painel.event.id, payload);
      } else {
        await criarEvento(payload);
      }
      const diaRef = painel?.date || form.dataEvento;
      setForm(FORM_VAZIO);
      await carregar();
      if (diaRef) setPainel({ mode: 'day', date: diaRef });
      else setPainel(null);
    } catch (e) {
      setError(e.message || 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const remover = async () => {
    if (!painel?.event?.id || !excluirEvento) return;
    if (!window.confirm('Excluir este evento?')) return;
    setSaving(true);
    try {
      await excluirEvento(painel.event.id);
      const diaRef = painel.date || String(painel.event?.dataEvento || '').slice(0, 10);
      await carregar();
      if (diaRef) setPainel({ mode: 'day', date: diaRef });
      else setPainel(null);
    } catch (e) {
      setError(e.message || 'Erro ao excluir.');
    } finally {
      setSaving(false);
    }
  };

  const renderMonthGrid = () => {
    const cells = getMonthGridCells(anchor.getFullYear(), anchor.getMonth() + 1);
    return (
      <Box>
        <Box
          display="grid"
          gridTemplateColumns="repeat(7, 1fr)"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {DIAS_SEMANA.map((d) => (
            <Box key={d} sx={{ p: 0.5, textAlign: 'center' }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                {d}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box display="grid" gridTemplateColumns="repeat(7, 1fr)">
          {cells.map((cell) => {
            const evs = mapa[cell.iso] || [];
            const isToday = cell.iso === hojeIso;
            const isSelectedDay = painel?.date === cell.iso;
            const temEventos = evs.length > 0;
            return (
              <Box
                key={cell.iso}
                onClick={() => abrirDia(cell.iso)}
                sx={{
                  minHeight: 92,
                  border: '2px solid',
                  borderColor: isSelectedDay ? 'primary.main' : temEventos ? 'primary.light' : 'divider',
                  bgcolor: !cell.inMonth
                    ? 'grey.50'
                    : isSelectedDay
                      ? 'rgba(25,118,210,0.2)'
                      : temEventos
                        ? 'rgba(25,118,210,0.08)'
                        : 'background.paper',
                  p: 0.5,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                  '&:hover': { bgcolor: 'rgba(25,118,210,0.14)' }
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={isToday ? 700 : 400}
                  sx={{
                    color: isToday ? 'primary.main' : cell.inMonth ? 'text.primary' : 'text.disabled',
                    display: 'inline-block',
                    bgcolor: isToday ? 'primary.light' : 'transparent',
                    borderRadius: '50%',
                    width: 22,
                    height: 22,
                    lineHeight: '22px',
                    textAlign: 'center'
                  }}
                >
                  {cell.day}
                </Typography>
                {evs.slice(0, 3).map((ev) => (
                  <EventoChip key={ev.id} ev={ev} onClick={abrirEditar} />
                ))}
                {evs.length > 3 && (
                  <Typography variant="caption" color="text.secondary">
                    +{evs.length - 3}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderWeekGrid = () => {
    const days = getWeekDays(anchor);
    return (
      <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(7, 1fr)' }} gap={1}>
        {days.map((d) => {
          const evs = mapa[d.iso] || [];
          const isToday = d.iso === hojeIso;
          return (
            <Paper
              key={d.iso}
              variant="outlined"
              onClick={() => abrirDia(d.iso)}
              sx={{
                p: 1,
                minHeight: 200,
                cursor: 'pointer',
                borderColor: isToday ? 'primary.main' : 'divider',
                borderWidth: isToday ? 2 : 1
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} color={isToday ? 'primary' : 'text.primary'}>
                {d.label} {d.date.getDate()}/{d.date.getMonth() + 1}
              </Typography>
              <Divider sx={{ my: 0.5 }} />
              {evs.map((ev) => (
                <EventoChip key={ev.id} ev={ev} onClick={abrirEditar} />
              ))}
              {evs.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  Clique para adicionar
                </Typography>
              )}
            </Paper>
          );
        })}
      </Box>
    );
  };

  const renderDayView = () => {
    const iso = toIsoDate(anchor);
    const evs = mapa[iso] || [];
    return (
      <Paper variant="outlined" sx={{ p: 2, minHeight: 320 }} onClick={() => abrirDia(iso)}>
        <Typography variant="h6" color="primary" gutterBottom>
          {anchor.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </Typography>
        {evs.length === 0 ? (
          <Typography color="text.secondary">Nenhum evento. Clique para criar.</Typography>
        ) : (
          evs.map((ev) => (
            <Paper
              key={ev.id}
              sx={{ p: 1.5, mb: 1, borderLeft: 4, borderColor: TIPO_COR[ev.tipo] || '#999', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                abrirEditar(ev);
              }}
            >
              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                <Typography fontWeight={600}>{ev.titulo}</Typography>
                <Chip size="small" label={TIPO_LABEL[ev.tipo] || ev.tipo} />
              </Box>
              {ev.descricao && (
                <Typography variant="body2" color="text.secondary">
                  {ev.descricao}
                </Typography>
              )}
            </Paper>
          ))
        )}
      </Paper>
    );
  };

  const renderYearView = () => {
    const y = anchor.getFullYear();
    return (
      <Grid container spacing={2}>
        {MESES.map((nome, idx) => {
          const m = idx + 1;
          const count = eventos.filter((e) => {
            const p = String(e.dataEvento).slice(0, 7);
            return p === `${y}-${String(m).padStart(2, '0')}`;
          }).length;
          return (
            <Grid item xs={6} sm={4} md={3} key={nome}>
              <Paper
                variant="outlined"
                sx={{ p: 2, textAlign: 'center', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => {
                  const d = new Date(y, idx, 1);
                  setAnchor(d);
                  setView('month');
                }}
              >
                <Typography fontWeight={700}>{nome}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {count} evento(s)
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderPainelLateral = () => {
    const fecharPainel = () => setPainel(null);

    if (!painel) {
      return (
        <Box sx={{ p: 2, color: 'text.secondary', textAlign: 'center', mt: 4 }}>
          <Typography variant="body2">
            Clique em uma data na grade para ver os eventos ou criar um novo.
          </Typography>
        </Box>
      );
    }

    const header = (
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle2" color="text.secondary">
          Detalhes do dia
        </Typography>
        <IconButton size="small" onClick={fecharPainel} aria-label="Fechar painel">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    );

    if (painel.mode === 'day') {
      const evs = mapa[painel.date] || [];
      return (
        <Box sx={{ p: 2 }}>
          {header}
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            {parseIso(painel.date).toLocaleDateString('pt-BR', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </Typography>
          <Button
            size="small"
            variant="contained"
            fullWidth
            sx={{ mb: 2, textTransform: 'none' }}
            onClick={() => abrirCriar(painel.date)}
          >
            + Novo evento neste dia
          </Button>
          {evs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhum evento nesta data.
            </Typography>
          ) : (
            evs.map((ev) => (
              <Paper
                key={ev.id}
                variant="outlined"
                sx={{
                  p: 1.5,
                  mb: 1,
                  cursor: 'pointer',
                  borderLeft: 4,
                  borderColor: TIPO_COR[ev.tipo] || '#999',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={() => abrirEditar(ev)}
              >
                <Box display="flex" gap={0.5} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" fontWeight={700}>
                    {ev.titulo}
                  </Typography>
                  <Chip size="small" label={TIPO_LABEL[ev.tipo] || ev.tipo} />
                </Box>
                {(ev.dataFim && ev.dataFim !== ev.dataEvento) && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Período: {parseIso(ev.dataEvento).toLocaleDateString('pt-BR')} —{' '}
                    {parseIso(ev.dataFim).toLocaleDateString('pt-BR')}
                  </Typography>
                )}
                {ev.numeroContrato && (
                  <Typography variant="caption" display="block">
                    Contrato {ev.numeroContrato}
                  </Typography>
                )}
                <Typography variant="caption" color="primary" display="block" sx={{ mt: 0.5 }}>
                  Clique para ver / editar
                </Typography>
              </Paper>
            ))
          )}
        </Box>
      );
    }

    if (painel.mode === 'view') {
      const ev = painel.event;
      return (
        <Box sx={{ p: 2 }}>
          {header}
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            {ev.titulo}
          </Typography>
          <Chip size="small" label={TIPO_LABEL[ev.tipo] || ev.tipo} sx={{ mb: 1 }} />
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {parseIso(ev.dataEvento).toLocaleDateString('pt-BR')}
            {(ev.dataFim && ev.dataFim !== ev.dataEvento) &&
              ` — ${parseIso(ev.dataFim).toLocaleDateString('pt-BR')}`}
          </Typography>
          {ev.descricao && <Typography variant="body2">{ev.descricao}</Typography>}
          {ev.numeroContrato && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Contrato: {ev.numeroContrato}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            Eventos automáticos (vencimento/alertas) não podem ser editados.
          </Typography>
          <Button size="small" sx={{ mt: 2 }} onClick={() => setPainel(null)}>
            Fechar
          </Button>
        </Box>
      );
    }

    const isEdit = painel.mode === 'edit';

    return (
      <Box sx={{ p: 2, overflow: 'auto' }}>
        {header}
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {isEdit ? 'Editar evento' : 'Novo evento'}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        <TextField
          label="Título"
          fullWidth
          size="small"
          margin="dense"
          value={form.titulo}
          onChange={(e) => setForm((p) => ({ ...p, titulo: e.target.value }))}
        />
        <TextField
          label="Data inicial"
          type="date"
          fullWidth
          size="small"
          margin="dense"
          InputLabelProps={{ shrink: true }}
          value={form.dataEvento}
          onChange={(e) =>
            setForm((p) => ({
              ...p,
              dataEvento: e.target.value,
              dataFim: p.dataFim && p.dataFim < e.target.value ? e.target.value : p.dataFim || e.target.value
            }))
          }
        />
        <TextField
          label="Data final (vários dias)"
          type="date"
          fullWidth
          size="small"
          margin="dense"
          InputLabelProps={{ shrink: true }}
          value={form.dataFim || form.dataEvento}
          onChange={(e) => setForm((p) => ({ ...p, dataFim: e.target.value }))}
          helperText="Deixe igual à inicial para evento de um dia"
        />
        <TextField
          label="Descrição"
          fullWidth
          size="small"
          margin="dense"
          multiline
          minRows={2}
          value={form.descricao}
          onChange={(e) => setForm((p) => ({ ...p, descricao: e.target.value }))}
        />
        {modoPrestador && contratos.length > 0 && (
          <TextField
            select
            label="Contrato"
            fullWidth
            size="small"
            margin="dense"
            value={form.numeroContrato}
            onChange={(e) => setForm((p) => ({ ...p, numeroContrato: e.target.value }))}
          >
            <MenuItem value="">—</MenuItem>
            {contratos.map((c) => (
              <MenuItem key={c.numeroContrato} value={c.numeroContrato}>
                {c.numeroContrato}
              </MenuItem>
            ))}
          </TextField>
        )}
        {modoPrestador ? (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.notificarContratos}
                  onChange={(e) => setForm((p) => ({ ...p, notificarContratos: e.target.checked }))}
                />
              }
              label="Notificar contratos"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.notificarFiscal}
                  onChange={(e) => setForm((p) => ({ ...p, notificarFiscal: e.target.checked }))}
                />
              }
              label="Notificar fiscal"
            />
          </>
        ) : (
          <>
            {catalogError && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {catalogError}
              </Alert>
            )}
            {loadingCatalog ? (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Carregando usuários e prestadores...
              </Typography>
            ) : (
              <>
                <Autocomplete
                  multiple
                  size="small"
                  options={usuarios}
                  getOptionLabel={(o) => `${o.nome} (${o.email || o.setor || ''})`}
                  value={usuarios.filter((u) => form.usuarioIds.includes(u.id))}
                  onChange={(_, vals) =>
                    setForm((p) => ({ ...p, usuarioIds: vals.map((v) => v.id) }))
                  }
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Usuários ISG" margin="dense" placeholder="Selecione um ou mais" />
                  )}
                  sx={{ mb: 1 }}
                />
                <Autocomplete
                  multiple
                  size="small"
                  options={prestadores}
                  getOptionLabel={(o) => o.razao_social || o.email || String(o.id)}
                  value={prestadores.filter((p) => form.prestadorIds.includes(p.id))}
                  onChange={(_, vals) =>
                    setForm((p) => ({ ...p, prestadorIds: vals.map((v) => v.id) }))
                  }
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Prestadores (portal)" margin="dense" placeholder="Selecione um ou mais" />
                  )}
                  sx={{ mb: 1 }}
                />
              </>
            )}
          </>
        )}
        <Autocomplete
          multiple
          freeSolo
          size="small"
          options={[]}
          value={form.emailsExtras || []}
          onChange={(_, vals) =>
            setForm((p) => ({
              ...p,
              emailsExtras: vals.map((v) => (typeof v === 'string' ? v : v))
            }))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="E-mails adicionais"
              margin="dense"
              placeholder="Digite e pressione Enter"
            />
          )}
        />
        <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
          <Button variant="contained" size="small" onClick={salvar} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
          {painel.date && (
            <Button size="small" onClick={() => setPainel({ mode: 'day', date: painel.date })}>
              Voltar ao dia
            </Button>
          )}
          <Button size="small" onClick={() => setPainel(null)}>
            Cancelar
          </Button>
          {isEdit && excluirEvento && (
            <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={remover} disabled={saving}>
              Excluir
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'background.default'
        }
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Typography variant="h6" component="span">
          Calendário
        </Typography>
        <IconButton onClick={onClose} aria-label="Fechar calendário">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={2} flexShrink={0}>
          <ToggleButtonGroup size="small" value={view} exclusive onChange={(_, v) => v && setView(v)}>
            <ToggleButton value="day">Dia</ToggleButton>
            <ToggleButton value="week">Semana</ToggleButton>
            <ToggleButton value="month">Mês</ToggleButton>
            <ToggleButton value="year">Ano</ToggleButton>
          </ToggleButtonGroup>

          <IconButton size="small" onClick={() => setAnchor(navigateAnchor(view, anchor, 'prev'))}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={600} sx={{ minWidth: 180, textAlign: 'center' }}>
            {range.label}
          </Typography>
          <IconButton size="small" onClick={() => setAnchor(navigateAnchor(view, anchor, 'next'))}>
            <ChevronRightIcon />
          </IconButton>
          <Button size="small" startIcon={<TodayIcon />} onClick={() => setAnchor(new Date())} sx={{ ml: 1 }}>
            Hoje
          </Button>

          <Box sx={{ flexGrow: 1 }} />
          {['vencimento_contrato', 'alerta_90', 'alerta_60', 'alerta_30', 'manual'].map((t) => (
            <Chip
              key={t}
              size="small"
              label={t === 'vencimento_contrato' ? 'Renovação' : TIPO_LABEL[t]}
              sx={{ bgcolor: TIPO_COR[t], color: '#fff', fontSize: 10 }}
            />
          ))}
        </Box>

        {error && !painel && (
          <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: isNarrow ? 'column' : 'row',
            flex: 1,
            minHeight: 0,
            gap: 0,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflow: 'auto',
              pr: painel && !isNarrow ? 0 : 0
            }}
          >
            <Box sx={{ position: 'relative', minHeight: isNarrow ? 360 : 'calc(100vh - 220px)' }}>
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.6)',
                    zIndex: 1
                  }}
                >
                  <CircularProgress size={32} />
                </Box>
              )}
              {view === 'month' && renderMonthGrid()}
              {view === 'week' && renderWeekGrid()}
              {view === 'day' && renderDayView()}
              {view === 'year' && renderYearView()}
            </Box>
          </Box>

          {painel && (
            <Paper
              variant="outlined"
              sx={{
                width: isNarrow ? '100%' : 400,
                maxWidth: '100%',
                flexShrink: 0,
                minHeight: isNarrow ? 280 : 0,
                maxHeight: isNarrow ? '40vh' : 'calc(100vh - 220px)',
                overflow: 'auto',
                borderLeft: isNarrow ? 0 : 1,
                borderTop: isNarrow ? 1 : 0,
                borderColor: 'divider',
                borderRadius: 0,
                bgcolor: 'background.paper'
              }}
            >
              {renderPainelLateral()}
            </Paper>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
